import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, of } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';
import {
  PlImpersonationService,
  selectCurrentUser,
  selectImpersonationUserData,
  selectIsImpersonating,
  User,
  UserGroupNames,
} from 'pl-core-services';
import { environment } from '@environments/environment';
import { FeatureFlags } from '@common/constant/feature-flags';
import { FeatureFlagService } from '@app/common/services/feature-flag.service';

/**
 * Any-of, deliberately: `School Staff Admins` covers Kanga-only and
 * Presence+Kanga admins, `CustomerAdmin` the Presence-only case. Not
 * `selectHasAllGroups` (as `showPlatformSwitcher$` uses) -- that matches only
 * the Presence+Kanga admin. Internal admins hold neither group, so Kanga stays
 * reachable for support.
 */
const ADMIN_GROUPS = [
  UserGroupNames.SCHOOL_STAFF_ADMINS,
  UserGroupNames.CUSTOMER_ADMIN,
];

/**
 * Bounds both waits below: a `canActivate` awaiting a value that never arrives
 * never returns, hanging the router on a blank page. The flag fails closed and
 * the user fails open, so either time-out settles on "stay in Kanga".
 */
export const REDIRECT_DECISION_TIMEOUT_MS = 5000;

/**
 * Sends Kanga admins who are not also providers to the Presence Admin View.
 *
 * Targets the Presence app root rather than `/admin/analytics`: edu-clients'
 * `injectHomeUrl` already resolves the destination and
 * `resolveActiveWorkspace()` pins the workspace, so duplicating those group
 * checks here would drift.
 *
 * While `redirectKangaAdminsToPresenceAdminDashboard` is on this applies to
 * every navigation. There is deliberately no one-shot marker, which would let
 * an admin stay in Kanga for the rest of the tab's life after the first one.
 *
 * Enabling this flag while edu-clients' `useAdminAnalytics` is off causes an
 * infinite cross-app redirect: `injectHomeUrl` falls back to the Kanga URL.
 */
@Injectable({ providedIn: 'root' })
export class PlAdminPresenceRedirectGuard implements CanActivate {
  constructor(
    private store: Store,
    private impersonationService: PlImpersonationService,
    private featureFlagService: FeatureFlagService
  ) {}

  async canActivate(): Promise<boolean> {
    // First, so that with the feature off this never waits on user data.
    if (!(await this.isRedirectEnabled())) {
      return true;
    }

    const user = await this.resolveUserWithGroups();

    // Groups never arrived: fail open rather than hold navigation open.
    if (!user) {
      return true;
    }

    // One escape hatch, so support can still reach Kanga in production.
    if (user.is_superuser) {
      return true;
    }

    if (!this.isAdmin(user)) {
      return true;
    }

    // An admin who is also a provider stays: the clinical workspace they need
    // exists only in Kanga. `pl-root` gives the provider group the same
    // precedence when picking a landing route.
    if (this.isProvider(user)) {
      return true;
    }

    // Impersonated sessions are redirected too. Both values are rehydrated from
    // sessionStorage by the auth reducer, so they are correct on a cold boot.
    const isImpersonating = await firstValueFrom(
      this.store.select(selectIsImpersonating)
    );
    const impersonationUserData = await firstValueFrom(
      this.store.select(selectImpersonationUserData)
    );

    this.redirectTo(
      this.impersonationService.buildAppUrl(
        environment.apps.presence,
        environment.presence_oidc_client_id,
        isImpersonating,
        impersonationUserData?.auth_db_user_uuid ?? null
      )
    );
    return false;
  }

  /**
   * `false` if the flag has not resolved within
   * {@link REDIRECT_DECISION_TIMEOUT_MS}. `isFeatureEnabled` emits `undefined`
   * until ConfigCat values load, hence the boolean filter.
   */
  private isRedirectEnabled(): Promise<boolean> {
    return firstValueFrom(
      this.featureFlagService
        .isFeatureEnabled(
          FeatureFlags.REDIRECT_KANGA_ADMINS_TO_PRESENCE_ADMIN_DASHBOARD
        )
        .pipe(
          filter((enabled): enabled is boolean => typeof enabled === 'boolean'),
          timeout({
            first: REDIRECT_DECISION_TIMEOUT_MS,
            with: () => of(false),
          })
        ),
      { defaultValue: false }
    );
  }

  /**
   * The current user once `groups` is populated (an empty array counts), or
   * `null` after {@link REDIRECT_DECISION_TIMEOUT_MS}.
   *
   * Not `selectLoadedCurrentUser`, which filters only on truthiness: Sirius
   * dispatches the user twice -- first from the raw OIDC payload, which carries
   * no `groups` or `is_superuser` -- so deciding on the first emission would
   * classify every admin as a non-admin.
   */
  private resolveUserWithGroups(): Promise<User | null> {
    return firstValueFrom(
      this.store.select(selectCurrentUser).pipe(
        filter((user): user is User => !!user && Array.isArray(user.groups)),
        timeout({
          first: REDIRECT_DECISION_TIMEOUT_MS,
          with: () => of(null),
        })
      ),
      { defaultValue: null }
    );
  }

  private isAdmin(user: User): boolean {
    return ADMIN_GROUPS.some((group) => user.groups?.includes(group));
  }

  // api-essentials pushes this group for every provider license, so it tracks
  // the license rather than a role label.
  private isProvider(user: User): boolean {
    return !!user.groups?.includes(UserGroupNames.SAAS_PROVIDER);
  }

  private redirectTo(url: string) {
    window.location.href = url;
  }
}
